import { LabeledRegion, drawLabelOnCanvas } from '../components/ImageAnnotator';
import { DetectedObject } from './detectApi';

// Must match the annotator's MAX_DIM and the backend's resize target.
const MAX_DIM = 1024;
const STROKE  = 'rgba(99,102,241,0.9)';
const FILL    = 'rgba(99,102,241,0.18)';

export function scaledCanvasDims(naturalW: number, naturalH: number): { w: number; h: number } {
  let w = naturalW, h = naturalH;
  if (w > MAX_DIM || h > MAX_DIM) {
    const s = MAX_DIM / Math.max(w, h);
    w = Math.round(w * s);
    h = Math.round(h * s);
  }
  return { w, h };
}

/** Convert 0–1000-normalized detection boxes to canvas-space labeled regions. */
export function boxesToRegions(
  objects: DetectedObject[],
  canvasW: number,
  canvasH: number,
): LabeledRegion[] {
  return objects.map(o => {
    const x1 = (o.box.xmin / 1000) * canvasW;
    const y1 = (o.box.ymin / 1000) * canvasH;
    const x2 = (o.box.xmax / 1000) * canvasW;
    const y2 = (o.box.ymax / 1000) * canvasH;
    return {
      id: o.id,
      label: o.label,
      tool: 'rectangle' as const,
      x1, y1, x2, y2,
      cx: (x1 + x2) / 2,
      cy: (y1 + y2) / 2,
    };
  });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

function drawRegions(ctx: CanvasRenderingContext2D, regions: LabeledRegion[], canvasW: number): void {
  for (const r of regions) {
    const minX = Math.min(r.x1, r.x2), minY = Math.min(r.y1, r.y2);
    const rw   = Math.abs(r.x2 - r.x1), rh = Math.abs(r.y2 - r.y1);
    ctx.save();
    ctx.strokeStyle = STROKE;
    ctx.fillStyle   = FILL;
    ctx.lineWidth   = 3;
    if (r.tool === 'freehand' && r.points?.length) {
      ctx.beginPath();
      ctx.moveTo(r.points[0].x, r.points[0].y);
      for (let i = 1; i < r.points.length; i++) ctx.lineTo(r.points[i].x, r.points[i].y);
      ctx.closePath(); ctx.fill(); ctx.stroke();
    } else if (r.tool === 'oval') {
      ctx.beginPath();
      ctx.ellipse(minX + rw / 2, minY + rh / 2, rw / 2, rh / 2, 0, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
    } else {
      ctx.beginPath(); ctx.rect(minX, minY, rw, rh); ctx.fill(); ctx.stroke();
    }
    ctx.restore();
  }
  for (const r of regions) {
    drawLabelOnCanvas(ctx, r.label, r.cx, r.cy, canvasW);
  }
}

/**
 * Draw labeled regions onto an image (scaled to MAX_DIM) and export as JPEG.
 * Regions must already be in canvas space (see boxesToRegions / annotator output).
 */
export async function burnRegionsOntoImage(
  imageBlob: Blob,
  regions: LabeledRegion[],
): Promise<{ file: File; url: string; canvasDims: { w: number; h: number } }> {
  const objUrl = URL.createObjectURL(imageBlob);
  try {
    const img = await loadImage(objUrl);
    const { w, h } = scaledCanvasDims(img.naturalWidth, img.naturalHeight);

    const canvas  = document.createElement('canvas');
    canvas.width  = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0, w, h);
    drawRegions(ctx, regions, w);

    const outBlob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Canvas export failed'))), 'image/jpeg', 0.88),
    );
    return {
      file: new File([outBlob], 'annotated.jpg', { type: 'image/jpeg' }),
      url:  URL.createObjectURL(outBlob),
      canvasDims: { w, h },
    };
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

/** Convenience: convert detection boxes to regions for this image, then burn. */
export async function burnDetectedObjects(
  imageBlob: Blob,
  objects: DetectedObject[],
): Promise<{ file: File; url: string; canvasDims: { w: number; h: number } }> {
  const objUrl = URL.createObjectURL(imageBlob);
  let dims: { w: number; h: number };
  try {
    const img = await loadImage(objUrl);
    dims = scaledCanvasDims(img.naturalWidth, img.naturalHeight);
  } finally {
    URL.revokeObjectURL(objUrl);
  }
  return burnRegionsOntoImage(imageBlob, boxesToRegions(objects, dims.w, dims.h));
}
